import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WindowedSelect from 'react-windowed-select';
import CostCentreSelects from './CostCentreSelects';
import { EWA_TYPE, NARRATION_MAX } from './saleVoucherConstants';
import { fmt3 } from './saleVoucherHelpers';
import { Field, Section, gridStyle, selectProps, toSelectOptions } from './formParts';

const dateInput = (value, onChange, disabled, clearable = true) => (
    <DatePicker
        selected={value} onChange={onChange} disabled={disabled} dateFormat="dd-MMM-yyyy"
        className="p-2 form-control w-100" isClearable={clearable && !disabled}
    />
);

const taxLabel = (t) => (t.taxCode === '0' ? 'No Tax' : `${t.taxCode} - ${t.description}`);

/** The header half of the document. Party selection is passed in as `partyPanel` so it sits under Type. */
const HeaderPanel = ({
    form, update, voucherNo, mode, disabled, errors, banner, voucherTypes, currencies, taxCodes, settings,
    amountReceived, partyPanel, onCurrency, onCostCentre, onCopyCC, onSetFromLines, onApplyTax,
}) => {
    const text = (key, label, extra = {}) => (
        <Field label={label}>
            <input className="p-2 form-control" disabled={disabled} value={form[key]} onChange={(e) => update({ [key]: e.target.value })} {...extra} />
        </Field>
    );
    const typeOptions = toSelectOptions(voucherTypes);
    const taxOptions = (taxCodes || []).map((t) => ({ value: t.taxCode, label: taxLabel(t) }));
    const currencyOptions = (currencies || []).map((c) => ({ value: c.code, label: c.code }));

    return (
        <Section title="Voucher Details">
            {banner && <div className="alert alert-danger py-2">{banner}</div>}
            {/* Two rows of three: Property and Unit (cost centres 1 and 2, they narrow the debit party list), then what the user types. */}
            <div style={gridStyle}>
                <CostCentreSelects levels={[1, 2]} values={form} onChange={onCostCentre} settings={settings} disabled={disabled} errors={errors} />
                <Field label="Voucher Date" required error={errors.postingDate}>
                    {dateInput(form.postingDate, (d) => update({ postingDate: d }), disabled, false)}
                </Field>
                <Field label="Type" required error={errors.voucherType}>
                    <WindowedSelect
                        {...selectProps} isDisabled={disabled} placeholder="--Select--"
                        value={typeOptions.find((o) => o.value === form.voucherType) || null}
                        options={typeOptions} onChange={(o) => update({ voucherType: o?.value || '' })}
                    />
                </Field>
                {form.voucherType === EWA_TYPE && (
                    <Field label="EWA Batch" required={mode === 'create'} error={errors.batchNo}>
                        <input className="p-2 form-control" disabled={disabled} value={form.batchNo} onChange={(e) => update({ batchNo: e.target.value })} />
                    </Field>
                )}
            </div>

            {/* Radios and the Chart of Accounts switch on the left, Voucher No on the right of the same row. */}
            <div className="mt-3 d-grid" style={{ gap: 18 }}>
                {React.cloneElement(partyPanel, {
                    trailing: (
                        <div className="d-flex align-items-start ms-auto" style={{ width: 340, maxWidth: '100%' }}>
                            <label className="form-label pt-1 start" style={{ minWidth: 130 }}>Voucher No</label>
                            <input className="p-2 form-control" disabled readOnly value={voucherNo || 'Auto'} />
                        </div>
                    ),
                })}
            </div>

            <div className="mt-3" style={gridStyle}>
                {text('refNo', 'Ref No')}
                {text('invoiceNo', 'Invoice No')}
                <Field label="Invoice Date">{dateInput(form.invoiceDate, (d) => update({ invoiceDate: d }), disabled)}</Field>
                <Field label="Due Date">{dateInput(form.dueDate, (d) => update({ dueDate: d }), disabled)}</Field>
                {text('orderNo', 'Order No')}
                <Field label="Order Date">{dateInput(form.orderDate, (d) => update({ orderDate: d }), disabled)}</Field>
                <Field label="Currency">
                    <WindowedSelect
                        {...selectProps} isDisabled={disabled}
                        value={currencyOptions.find((o) => o.value === form.currency) || null}
                        options={currencyOptions} onChange={(o) => onCurrency(o?.value || '')}
                    />
                </Field>
                <Field label="Conversion Rate" error={errors.conversionRate}>
                    <input
                        type="number" min="0" step="any" className="p-2 form-control text-end" disabled={disabled}
                        value={form.conversionRate} onChange={(e) => update({ conversionRate: e.target.value })}
                    />
                </Field>
                <Field label="Amount" required error={errors.amount}>
                    <div className="d-flex gap-2">
                        <input
                            type="number" min="0" step="0.001" className="p-2 form-control text-end" disabled={disabled}
                            value={form.amount} onChange={(e) => update({ amount: e.target.value })}
                        />
                        {!disabled && (
                            <button type="button" className="btn btn-outline-secondary btn-sm text-nowrap" onClick={onSetFromLines}>= Set from lines</button>
                        )}
                    </div>
                    {mode === 'edit' && <div className="small text-muted mt-1">Amount received: {fmt3(amountReceived)}</div>}
                </Field>
                <Field label="Narration" className="narration-field" error={errors.narration}>
                    <textarea
                        className="p-2 form-control" rows={2} disabled={disabled} maxLength={NARRATION_MAX + 200}
                        value={form.narration} onChange={(e) => update({ narration: e.target.value })}
                    />
                    <div className={`small text-end ${form.narration.length > NARRATION_MAX ? 'text-danger' : 'text-muted'}`}>
                        {form.narration.length}/{NARRATION_MAX}
                    </div>
                </Field>
                <Field label="Tax Code">
                    <WindowedSelect
                        {...selectProps} isDisabled={disabled}
                        value={taxOptions.find((o) => o.value === (form.taxCode || '0')) || null}
                        options={taxOptions} onChange={(o) => update({ taxCode: o?.value || '0' }, { taxTouched: true })}
                    />
                    <div className="d-flex align-items-center gap-3 mt-2">
                        <div className="form-check m-0 d-flex align-items-center gap-2">
                            <input
                                type="checkbox" className="form-check-input m-0" id="taxInclusive" disabled={disabled}
                                checked={form.taxInclusive} onChange={(e) => update({ taxInclusive: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="taxInclusive">Tax Inclusive</label>
                        </div>
                        {!disabled && <button type="button" className="btn btn-link btn-sm p-0" onClick={onApplyTax}>Apply tax to all rows</button>}
                    </div>
                </Field>
            </div>

            <div className="mt-3" style={gridStyle}>
                <CostCentreSelects levels={[3, 4]} values={form} onChange={onCostCentre} settings={settings} disabled={disabled} errors={errors} />
                <div className="form-check m-0 d-flex align-items-center gap-2">
                    <input
                        type="checkbox" className="form-check-input m-0" id="copyCC" disabled={disabled}
                        checked={form.copyCC} onChange={(e) => onCopyCC(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="copyCC">Copy CC to all rows</label>
                </div>
            </div>
        </Section>
    );
};

export default HeaderPanel;
